import { Component, h } from 'preact'
import Table from '../table/Table'
import axios from 'axios'
import AnalyticsDateFormatter from './AnalyticsDateFormatter'
import { Sort } from '../table/types'
import HappyFace from '../topic-helpfulness/assets/happy'
import ConfusedFace from '../topic-helpfulness/assets/confused'
import WearyFace from '../topic-helpfulness/assets/weary'
import { Loader } from './Loader'
import EmptyTable from './EmptyTable'
import { EmptyResults } from './EmptyResults'

interface Props {
  startDate: Date,
  endDate: Date,
  selectedRoles: Array<string>,
  selectedContentTypes: Array<string>,
  selectedCategories: Array<string>
}

interface SortingObject {
  [key: string]: any
}

interface PopularTopicRepresentation {
  title: string,
  url: string,
  replies: number,
  likes: number,
  helpful: number,
  neutral: number,
  notHelpful: number,
  helpfulnessScore: number
}

interface State {
  popularTopics: Array<PopularTopicRepresentation>,
  sorting: Sort,
  isLoading: boolean
}

class PopularTopicsTable extends Component<Props, State> {
  constructor () {
    super()
    this.setState({
      popularTopics: [],
      sorting: {
        order: 'replies',
        direction: 'desc'
      },
      isLoading: true
    })
  }

  async componentDidUpdate (prevProps: Props, prevState: State) {
    if (this.props.startDate !== prevProps.startDate || this.props.endDate !== prevProps.endDate || this.props.selectedRoles !== prevProps.selectedRoles || this.props.selectedContentTypes !== prevProps.selectedContentTypes || this.props.selectedCategories !== prevProps.selectedCategories) {
      await this.fetchPopularTopics()
    }
  }

  async fetchPopularTopics () {
    this.setState({ isLoading: true })
    try {
      const res = await this.fetchData('/analyticsV2/fetchPopularTopics')
      this.setState({
        popularTopics: res.data,
        isLoading: false
      })
    } catch {
      this.setState({ isLoading: false })
    }
  }

  async fetchData (url: string) {
    const { startDate, endDate, selectedRoles, selectedContentTypes, selectedCategories } = this.props
    const filterOptions = {
      start: AnalyticsDateFormatter.formatDate(startDate),
      end: AnalyticsDateFormatter.formatDate(endDate, false),
      roles: selectedRoles,
      contentTypes: selectedContentTypes,
      categories: selectedCategories
    }
    return axios.post(url, {
      filters: filterOptions
    })
  }

  titleCell (topic: PopularTopicRepresentation) {
    return <a href={`/${topic.url}`}>{topic.title}</a>
  }

  helpfulnessCell (topic: PopularTopicRepresentation) {
    if (!topic) {
      return ''
    }
    const helpfulnessScore = topic.helpfulnessScore < 0 ? '-' : topic.helpfulnessScore
    return <div className='helpfulness'>
      <div>{`${helpfulnessScore}%`}</div>
      <div>{topic.helpful} <HappyFace /></div>
      <div>{topic.neutral} <ConfusedFace /></div>
      <div>{topic.notHelpful} <WearyFace /></div>
    </div>
  }

  getModel () {
    return [
      { header: 'Title', accessor: 'title', cell: this.titleCell, size: 'm' },
      { header: 'Replies', accessor: 'replies', size: 'xs-right', sorting: 'replies' },
      { header: 'Likes', accessor: 'likes', size: 'xs-right', sorting: 'likes' },
      { header: 'Content helpfulness score', accessor: 'helpfulness', cell: this.helpfulnessCell, size: 'right', sorting: 'helpfulnessScore' }
    ]
  }

  render (_: Props, { popularTopics, sorting, isLoading }: State) {
    return <div className='dashboard-table-wrapper'>
      {isLoading ? <EmptyTable model={this.getModel()} children={<Loader parentCustomClass='dashboard-table-loader' />} />
        : popularTopics.length > 0 ? <Table model={this.getModel()} rows={popularTopics}
          classes={['popular_topics__table']}
          onSortChange={(attr) => {
            let newSorting: Sort
            if (sorting && sorting.order === attr) {
              newSorting = {
                order: attr,
                direction: sorting.direction === 'asc' ? 'desc' : 'asc'
              }
            } else {
              newSorting = {
                order: attr,
                direction: 'asc'
              }
            }
            popularTopics.sort((a: SortingObject, b: SortingObject) => {
              if (a[attr] > b[attr]) return 1 * (newSorting.direction === 'asc' ? 1 : -1)
              if (a[attr] < b[attr]) return -1 * (newSorting.direction === 'asc' ? 1 : -1)
              return 0
            })
            this.setState({
              sorting: newSorting
            })
          }}
          sort={sorting}
        /> : <EmptyTable model={this.getModel()} children={<EmptyResults />} />}
    </div>
  }
}

export default PopularTopicsTable
